import React, { useState, useEffect, useRef, useCallback } from 'react';
import './BugReportModal.css';
import {
  popoverContainerStyle,
  popoverLabelStyle,
  popoverSelectStyle,
  popoverButtonContainerStyle,
  getCancelButtonStyle,
  getSubmitButtonStyle
} from './PopoverStyles';
import { useLanguage } from '../../contexts/LanguageContext';
import ConfirmationModal from './ConfirmationModal';

/**
 * Bug Report Modal Component for Beta Testers
 * Issue #185: Let beta testers report bugs
 * Issue #337: Screenshot upload capability
 *
 * Features:
 * - Dynamic step inputs for reproduction steps
 * - Technical details (connection quality, OS)
 * - Issue #337: Screenshot upload with preview (max 2 MB)
 * - Draft save/restore with localStorage
 * - Confirmation modal on cancel
 * - Designed to be removable for launch version
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {Function} onClose - Close callback
 * @param {Function} onSubmit - Submit callback (bugReport) => Promise
 * @param {boolean} isSubmitting - Whether submission is in progress
 */

const MAX_SCREENSHOT_SIZE = 2 * 1024 * 1024; // 2 MB
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const BugReportModal = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false
}) => {
  const { t } = useLanguage();
  const DRAFT_STORAGE_KEY = 'universe_bug_report_draft';

  // Form state
  const [steps, setSteps] = useState(['', '', '']);
  const DEFAULT_STEPS_COUNT = 3; // Issue #187: Track default steps for delete button visibility
  // Issue #193: Expected and actual result fields
  const [expectedResult, setExpectedResult] = useState('');
  const [actualResult, setActualResult] = useState('');
  const [connectionQuality, setConnectionQuality] = useState('');
  const [os, setOs] = useState('');
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [draftMessage, setDraftMessage] = useState('');
  // Issue #261: Track last saved state to detect unsaved changes
  const [lastSavedState, setLastSavedState] = useState(null);
  // Issue #337: Screenshot state
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [screenshotError, setScreenshotError] = useState('');

  const modalRef = useRef(null);
  const firstInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load draft from localStorage when modal opens
  useEffect(() => {
    if (isOpen) {
      const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (savedDraft) {
        try {
          const draft = JSON.parse(savedDraft);
          if (draft.steps) setSteps(draft.steps);
          // Issue #193: Load expected and actual result from draft
          if (draft.expectedResult) setExpectedResult(draft.expectedResult);
          if (draft.actualResult) setActualResult(draft.actualResult);
          if (draft.connectionQuality) setConnectionQuality(draft.connectionQuality);
          if (draft.os) setOs(draft.os);
          // Issue #261: Set last saved state to loaded draft
          setLastSavedState(draft);
        } catch (e) {
          // Invalid draft, ignore
        }
      } else {
        // Issue #261: No draft exists, reset last saved state
        setLastSavedState(null);
      }
      // Focus first input after modal opens
      setTimeout(() => firstInputRef.current?.focus(), 10);
    }
  }, [isOpen]);

  // Issue #337: Cleanup screenshot preview URL on unmount
  useEffect(() => {
    return () => {
      if (screenshotPreview) {
        URL.revokeObjectURL(screenshotPreview);
      }
    };
  }, [screenshotPreview]);

  // Handle keyboard navigation and focus trap
  const handleKeyDown = useCallback((e) => {
    if (!isOpen || showCancelConfirmation) return;

    if (e.key === 'Escape' && !isSubmitting) {
      e.preventDefault();
      handleCancelClick();
      return;
    }

    // Focus trap
    if (e.key === 'Tab') {
      const focusableElements = modalRef.current?.querySelectorAll(
        'input:not([disabled]), select:not([disabled]), button:not([disabled])'
      );

      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }
  }, [isOpen, isSubmitting, showCancelConfirmation]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  // Handle step input change with auto-resize
  const handleStepChange = (index, value, event) => {
    const newSteps = [...steps];
    newSteps[index] = value;
    setSteps(newSteps);
    // Auto-resize textarea
    if (event && event.target) {
      event.target.style.height = 'auto';
      event.target.style.height = event.target.scrollHeight + 'px';
    }
  };

  // Add new step
  const handleAddStep = () => {
    setSteps([...steps, '']);
  };

  // Issue #187: Remove step (only for added steps, not default ones)
  const handleRemoveStep = (index) => {
    const newSteps = steps.filter((_, i) => i !== index);
    setSteps(newSteps);
  };

  // Issue #337: Handle screenshot selection
  const handleScreenshotChange = (e) => {
    const file = e.target.files?.[0];
    setScreenshotError('');

    if (!file) {
      return;
    }

    // Validate file type
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setScreenshotError(t('bugReport.screenshotInvalidType'));
      e.target.value = '';
      return;
    }

    // Validate file size
    if (file.size > MAX_SCREENSHOT_SIZE) {
      setScreenshotError(t('bugReport.screenshotTooLarge'));
      e.target.value = '';
      return;
    }

    // Revoke old preview URL
    if (screenshotPreview) {
      URL.revokeObjectURL(screenshotPreview);
    }

    setScreenshot(file);
    setScreenshotPreview(URL.createObjectURL(file));
  };

  // Issue #337: Remove screenshot
  const handleRemoveScreenshot = () => {
    if (screenshotPreview) {
      URL.revokeObjectURL(screenshotPreview);
    }
    setScreenshot(null);
    setScreenshotPreview(null);
    setScreenshotError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Save draft to localStorage
  const handleSaveDraft = () => {
    const draft = {
      steps,
      // Issue #193: Include expected and actual result in draft
      expectedResult,
      actualResult,
      connectionQuality,
      os
    };
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
    // Issue #261: Update last saved state to current state
    setLastSavedState(draft);
    setDraftMessage(t('bugReport.draftSaved'));
    setTimeout(() => setDraftMessage(''), 2000);
  };

  // Clear draft from localStorage
  const clearDraft = () => {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
  };

  // Issue #261: Check if current form state differs from last saved state
  const hasUnsavedChanges = useCallback(() => {
    const currentState = {
      steps,
      expectedResult,
      actualResult,
      connectionQuality,
      os
    };

    // If no saved state exists, check if form has any content
    if (!lastSavedState) {
      return steps.some(s => s.trim()) || expectedResult.trim() || actualResult.trim() || connectionQuality || os || screenshot;
    }

    // Compare current state with last saved state
    const savedSteps = lastSavedState.steps || ['', '', ''];
    const savedExpectedResult = lastSavedState.expectedResult || '';
    const savedActualResult = lastSavedState.actualResult || '';
    const savedConnectionQuality = lastSavedState.connectionQuality || '';
    const savedOs = lastSavedState.os || '';

    // Check if steps array differs (length or content)
    if (steps.length !== savedSteps.length) return true;
    for (let i = 0; i < steps.length; i++) {
      if (steps[i] !== savedSteps[i]) return true;
    }

    // Check other fields
    if (expectedResult !== savedExpectedResult) return true;
    if (actualResult !== savedActualResult) return true;
    if (connectionQuality !== savedConnectionQuality) return true;
    if (os !== savedOs) return true;
    // Issue #337: Screenshot counts as unsaved change
    if (screenshot) return true;

    return false;
  }, [steps, expectedResult, actualResult, connectionQuality, os, lastSavedState, screenshot]);

  // Handle cancel click - show confirmation only if there are unsaved changes
  const handleCancelClick = () => {
    // Issue #261: Only show confirmation if there are unsaved changes
    if (hasUnsavedChanges()) {
      setShowCancelConfirmation(true);
    } else {
      onClose();
    }
  };

  // Confirm cancel - close without saving
  // Issue #256: Don't clear draft on cancel - draft should only be cleared on successful submission
  // This allows users to save a draft, close the modal, and return to their saved draft later
  const handleConfirmCancel = () => {
    setShowCancelConfirmation(false);
    resetForm();
    onClose();
  };

  // Reset form to initial state
  const resetForm = () => {
    setSteps(['', '', '']);
    // Issue #193: Reset expected and actual result fields
    setExpectedResult('');
    setActualResult('');
    setConnectionQuality('');
    setOs('');
    // Issue #261: Reset last saved state
    setLastSavedState(null);
    // Issue #337: Reset screenshot
    handleRemoveScreenshot();
  };

  // Handle form submission
  const handleSubmit = () => {
    // Validate mandatory fields
    if (!steps[0] || !steps[0].trim()) {
      alert(t('bugReport.step1Required'));
      return;
    }

    if (!connectionQuality) {
      alert(t('bugReport.connectionRequired'));
      return;
    }

    if (!os) {
      alert(t('bugReport.osRequired'));
      return;
    }

    // Filter out empty steps
    const filledSteps = steps.filter(s => s.trim());

    // Issue #193: Include expected and actual result in bug report (optional fields)
    // Issue #337: Include screenshot file
    const bugReport = {
      steps: filledSteps,
      expected_result: expectedResult.trim() || null,
      actual_result: actualResult.trim() || null,
      connection_quality: connectionQuality,
      os: os,
      screenshot: screenshot || null
    };

    onSubmit(bugReport).then(() => {
      clearDraft();
      resetForm();
    });
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="modal-overlay"
        onClick={isSubmitting ? undefined : handleCancelClick}
        role="presentation"
      >
        <div
          ref={modalRef}
          onClick={(e) => e.stopPropagation()}
          style={{ ...popoverContainerStyle, maxHeight: '90vh', overflow: 'auto' }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="bug-report-modal-title"
        >
          {/* Header with bug icon, title and BETA chip */}
          <div className="bug-report-header">
            <div className="bug-report-title-group">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="bug-icon">
                <path d="M8 2L6 4M16 2L18 4M3 10H5M19 10H21M5 19L7 17M19 19L17 17M12 22V19M9 19H15M12 7C9.24 7 7 9.24 7 12V15C7 17.76 9.24 20 12 20C14.76 20 17 17.76 17 15V12C17 9.24 14.76 7 12 7ZM9 12H15M9 15H15" stroke="#1C274C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span id="bug-report-modal-title" className="bug-report-title">
                {t('bugReport.title')}
              </span>
              <span className="bug-report-beta-chip">
                {t('bugReport.beta')}
              </span>
            </div>
            <button
              className="bug-report-close-button"
              onClick={handleCancelClick}
              disabled={isSubmitting}
              aria-label={t('close')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          {/* Steps section title */}
          <p style={{ ...popoverLabelStyle, fontWeight: 600, fontSize: '15px' }}>
            {t('bugReport.stepsTitle')}
          </p>

          {/* Dynamic step inputs */}
          <div className="bug-report-steps">
            {steps.map((step, index) => (
              <div key={index} className="bug-report-step-row">
                <span className="bug-report-step-label">
                  {t('bugReport.step')} {index + 1}.{index === 0 && <span className="required-marker" aria-hidden="true"> *</span>}
                </span>
                <textarea
                  ref={index === 0 ? firstInputRef : null}
                  value={step}
                  onChange={(e) => handleStepChange(index, e.target.value, e)}
                  placeholder={t('bugReport.stepPlaceholder')}
                  className="bug-report-step-input"
                  aria-label={`${t('bugReport.step')} ${index + 1}${index === 0 ? ' (' + t('bugReport.required') + ')' : ''}`}
                  aria-required={index === 0}
                  disabled={isSubmitting}
                  rows={1}
                  style={{ overflow: 'hidden', resize: 'none' }}
                />
                {/* Issue #187: Delete button for added steps only */}
                {index >= DEFAULT_STEPS_COUNT && (
                  <button
                    type="button"
                    onClick={() => handleRemoveStep(index)}
                    className="bug-report-remove-step-button"
                    disabled={isSubmitting}
                    aria-label={t('bugReport.removeStep')}
                  >
                    &#8722;
                  </button>
                )}
              </div>
            ))}

            {/* Add more steps button */}
            <div className="bug-report-add-step-row">
              <span className="bug-report-add-step-label">
                {t('bugReport.addStep')}
              </span>
              <button
                type="button"
                onClick={handleAddStep}
                className="bug-report-add-step-button"
                disabled={isSubmitting}
                aria-label={t('bugReport.addStep')}
              >
                +
              </button>
            </div>
          </div>

          {/* Issue #193: Expected and Actual Result fields */}
          <div className="bug-report-result-fields">
            {/* Expected Result */}
            <div className="bug-report-result-field">
              <label className="bug-report-result-label" htmlFor="expected-result">
                {t('bugReport.expectedResult')}
              </label>
              <textarea
                id="expected-result"
                value={expectedResult}
                onChange={(e) => {
                  setExpectedResult(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = e.target.scrollHeight + 'px';
                }}
                placeholder={t('bugReport.expectedResultPlaceholder')}
                className="bug-report-step-input"
                aria-label={t('bugReport.expectedResult')}
                disabled={isSubmitting}
                rows={1}
                style={{ overflow: 'hidden', resize: 'none' }}
              />
            </div>

            {/* Actual Result */}
            <div className="bug-report-result-field">
              <label className="bug-report-result-label" htmlFor="actual-result">
                {t('bugReport.actualResult')}
              </label>
              <textarea
                id="actual-result"
                value={actualResult}
                onChange={(e) => {
                  setActualResult(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = e.target.scrollHeight + 'px';
                }}
                placeholder={t('bugReport.actualResultPlaceholder')}
                className="bug-report-step-input"
                aria-label={t('bugReport.actualResult')}
                disabled={isSubmitting}
                rows={1}
                style={{ overflow: 'hidden', resize: 'none' }}
              />
            </div>
          </div>

          {/* Issue #337: Screenshot upload section */}
          <div className="bug-report-screenshot-section">
            <label className="bug-report-result-label">
              {t('bugReport.screenshot')}
            </label>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleScreenshotChange}
              className="sr-only"
              id="bug-report-screenshot-input"
              aria-label={t('bugReport.addScreenshot')}
              disabled={isSubmitting}
            />

            {screenshotPreview ? (
              /* Screenshot preview */
              <div className="bug-report-screenshot-preview">
                <img
                  src={screenshotPreview}
                  alt={t('bugReport.screenshot')}
                  className="bug-report-screenshot-image"
                />
                <button
                  type="button"
                  onClick={handleRemoveScreenshot}
                  className="bug-report-screenshot-remove"
                  disabled={isSubmitting}
                  aria-label={t('bugReport.removeScreenshot')}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ) : (
              /* Upload button */
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="bug-report-screenshot-button"
                disabled={isSubmitting}
                aria-label={t('bugReport.addScreenshot')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M23 19C23 19.5304 22.7893 20.0391 22.4142 20.4142C22.0391 20.7893 21.5304 21 21 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V8C1 7.46957 1.21071 6.96086 1.58579 6.58579C1.96086 6.21071 2.46957 6 3 6H7L9 3H15L17 6H21C21.5304 6 22.0391 6.21071 22.4142 6.58579C22.7893 6.96086 23 7.46957 23 8V19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="13" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{t('bugReport.addScreenshot')}</span>
              </button>
            )}

            {/* File size hint */}
            <span className="bug-report-screenshot-hint">
              {t('bugReport.screenshotHint')}
            </span>

            {/* Error message */}
            {screenshotError && (
              <p className="bug-report-screenshot-error" role="alert">
                {screenshotError}
              </p>
            )}
          </div>

          {/* Technical details section title */}
          <p style={{ ...popoverLabelStyle, fontWeight: 600, fontSize: '15px' }}>
            {t('bugReport.technicalTitle')}
          </p>

          {/* Technical details */}
          <div className="bug-report-technical">
            {/* Connection quality dropdown */}
            <div className="bug-report-field">
              <label className="bug-report-field-label">
                {t('bugReport.connectionQuality')}<span className="required-marker" aria-hidden="true"> *</span>
              </label>
              <select
                value={connectionQuality}
                onChange={(e) => setConnectionQuality(e.target.value)}
                style={{
                  ...popoverSelectStyle,
                  color: connectionQuality ? 'var(--text-primary)' : '#878787'
                }}
                disabled={isSubmitting}
                aria-label={t('bugReport.connectionQuality')}
                aria-required="true"
              >
                <option value="">--</option>
                <option value="good">{t('bugReport.connectionGood')}</option>
                <option value="medium">{t('bugReport.connectionMedium')}</option>
                <option value="bad">{t('bugReport.connectionBad')}</option>
              </select>
            </div>

            {/* OS dropdown */}
            <div className="bug-report-field">
              <label className="bug-report-field-label">
                {t('bugReport.os')}<span className="required-marker" aria-hidden="true"> *</span>
              </label>
              <select
                value={os}
                onChange={(e) => setOs(e.target.value)}
                style={{
                  ...popoverSelectStyle,
                  color: os ? 'var(--text-primary)' : '#878787'
                }}
                disabled={isSubmitting}
                aria-label={t('bugReport.os')}
                aria-required="true"
              >
                <option value="">--</option>
                <option value="android">Android</option>
                <option value="ios">iOS</option>
                <option value="web">Web</option>
              </select>
            </div>
          </div>

          {/* Draft saved message */}
          {draftMessage && (
            <p className="bug-report-draft-message" role="status" aria-live="polite">
              {draftMessage}
            </p>
          )}

          {/* Buttons */}
          <div style={popoverButtonContainerStyle}>
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={isSubmitting}
              style={getCancelButtonStyle(isSubmitting)}
            >
              {t('bugReport.save')}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              style={getSubmitButtonStyle(isSubmitting)}
            >
              {isSubmitting ? t('bugReport.submitting') : t('bugReport.submit')}
            </button>
          </div>
        </div>
      </div>

      {/* Cancel confirmation modal */}
      <ConfirmationModal
        isOpen={showCancelConfirmation}
        onClose={() => setShowCancelConfirmation(false)}
        onConfirm={handleConfirmCancel}
        title={t('bugReport.cancelTitle')}
        message={t('bugReport.cancelMessage')}
        confirmText={t('bugReport.cancelConfirm')}
        confirmingText={t('bugReport.cancelConfirm')}
        cancelText={t('bugReport.cancelStay')}
        variant="default"
      />
    </>
  );
};

export default BugReportModal;
